import { useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { apiErrorText, readApiErrorResponse } from "@/lib/api-error";
import { deviceLabel, formatDeviceTime } from "@/lib/device-label";
import { apiFetch } from "@/services/http/api-fetch";
import type { SignedInDevice } from "@/services/api/auth";

type MemberDevicesPanelProps = { userId: string; name: string };

/** S3 V-8: the devices screen's own wording (e.g. NOT_A_STUDENT) before the members section's. */
const DEVICE_ERROR_NAMESPACES = ["admin.devicesErrors", "admin.errors"] as const;

const secondaryButton =
  "rounded-md border border-[var(--border-strong)] px-3 py-1.5 text-sm hover:bg-[var(--surface-3)]";

/**
 * H3: a school admin sees where a student is signed in (browser, system, times — no IPs) and can
 * sign them out of every device ("reset"). Audited on the server.
 */
export function MemberDevicesPanel({ userId, name }: MemberDevicesPanelProps) {
  const t = useTranslations("admin");
  const tDevices = useTranslations("auth.devices");
  const tRoot = useTranslations();
  const locale = useLocale();
  const [open, setOpen] = useState(false);
  const [devices, setDevices] = useState<SignedInDevice[] | null>(null);
  const [limit, setLimit] = useState<number | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const path = `/v1/tenant/members/${userId}/devices`;

  async function load() {
    setError(null);
    try {
      const res = await apiFetch(path);
      if (!res.ok) throw await readApiErrorResponse(res);
      const json = (await res.json()) as {
        data: { devices: SignedInDevice[]; deviceLimit: number };
      };
      setDevices(json.data.devices);
      setLimit(json.data.deviceLimit);
    } catch (err) {
      setError(
        apiErrorText(err, tRoot, "admin.devicesLoadFailed", { namespace: DEVICE_ERROR_NAMESPACES }),
      );
    }
  }

  function toggle() {
    const next = !open;
    setOpen(next);
    setMessage(null);
    if (next) void load();
  }

  async function signOutAll() {
    if (!window.confirm(t("devicesConfirm", { name }))) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch(path, { method: "DELETE" });
      if (!res.ok) throw await readApiErrorResponse(res);
      const json = (await res.json()) as { data: { signedOut: number } };
      setMessage(t("devicesSignedOut", { count: json.data.signedOut }));
      await load();
    } catch (err) {
      setError(
        apiErrorText(err, tRoot, "admin.devicesResetFailed", {
          namespace: DEVICE_ERROR_NAMESPACES,
        }),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="basis-full" data-testid={`member-devices-${userId}`}>
      <button
        type="button"
        className={secondaryButton}
        aria-expanded={open}
        aria-label={t("devicesButtonLabel", { name })}
        data-testid={`member-devices-toggle-${userId}`}
        onClick={toggle}
      >
        {open ? t("devicesHide") : t("devicesButton")}
      </button>
      {open ? (
        <div className="mt-3 space-y-2" data-testid={`member-devices-panel-${userId}`}>
          {limit !== null ? (
            <p className="text-xs text-[var(--text-muted)]">
              {limit > 0 ? t("devicesLimit", { limit }) : t("devicesNoLimit")}
            </p>
          ) : null}
          {devices && devices.length === 0 ? (
            <p className="text-sm text-[var(--text-muted)]">{t("devicesNone")}</p>
          ) : null}
          {devices && devices.length > 0 ? (
            <ul className="space-y-1 text-sm">
              {devices.map((device) => (
                <li key={device.id} data-testid={`member-device-${device.id}`}>
                  <span className="font-medium">{deviceLabel(tDevices, device)}</span>
                  {device.place ? (
                    <span className="text-[var(--text-muted)]"> · {device.place}</span>
                  ) : null}{" "}
                  <span className="text-[var(--text-muted)]">
                    {tDevices("lastActive", {
                      time: formatDeviceTime(device.lastActiveAt, locale),
                    })}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
          {devices && devices.length > 0 ? (
            <button
              type="button"
              className={secondaryButton + " text-red-700"}
              disabled={busy}
              data-testid={`member-devices-sign-out-all-${userId}`}
              onClick={() => void signOutAll()}
            >
              {t("devicesSignOutAll")}
            </button>
          ) : null}
          {message ? (
            <p role="status" className="text-sm" data-testid={`member-devices-message-${userId}`}>
              {message}
            </p>
          ) : null}
          {error ? (
            <p role="alert" className="text-sm text-red-600">
              {error}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
